import { getClassroomApiUrl } from "@/js/utils/getBackendUrl";
import * as clsApi from "./classroomApi";

export class FocusValidationConflictError extends Error {
	duplicatedName?: boolean;
	conflictingFocusTabSignatures?: string[];
	conflictingFocusTabUrls?: string[];

	constructor(duplicatedName: boolean, conflictingFocusTabSignatures: string[], conflictingFocusTabUrls: string[]) {
		super();
		this.message = "Focus validation against School Manager Locked rules failed";
		this.name = "FocusValidationConflictError";
		this.duplicatedName = duplicatedName;
		this.conflictingFocusTabSignatures = conflictingFocusTabSignatures;
		this.conflictingFocusTabUrls = conflictingFocusTabUrls;
	}
}

export type FocusTabDTO = {
	/** ID of the focus if it already exists. Omit to create a new focus resource. */
	id?: string;
	signature?: string;
	url?: string;
	openTab: boolean;
};

export interface FocusDTO {
	id: string;
	name: string;
	focusTabs: FocusTabDTO[];
	/**
	 * Whether or not the focus is applied to a class with "Locked Focus" enabled. Independent of whether the class/focus is active.
	 */
	tabsLocked?: boolean;
	/**
	 * When the focus was last used. Omitted if focus has never been used.
	 */
	lastUsed?: number;
	createdAt: number;
	active: boolean;
	/**
	 * List of student's stable IDs that have this focus applied. Omitted if focus is not active.
	 */
	studentIds?: string[];
}

export interface GetFocusLibraryResponseBody {
	focusLibrary: FocusDTO[];
}

export interface GetFocusesAppliedToClassResponseBody {
	appliedFocuses: FocusDTO[];
}

export interface CreateOrUpdateFocusRequestBody {
	/** ID of the focus to be updated. Omit if creating a new focus. */
	id?: string;
	/** The tabs the student will be focused on */
	focusTabs: FocusTabDTO[];
	/** Whether or not the focus has "Locked Focus" enabled. */
	tabsLocked: boolean;
	/** Stable ID of the active class the focus is being applied to. This is also the stable ID of the policy for the class. */
	classId?: string;
	/** Name is only required when saving a focus to the library */
	name?: string;
	/** Student IDs are only required when immediately applying the focus */
	studentIds?: string[];
	/** Qoria User IDs are only required when immediately applying the focus */
	userIds?: string[];
}

export interface CreateOrUpdateFocusOkResponseBody {
	/** ID of the focus */
	id: string;
	/** Tabs the student is focused on */
	focusTabs: FocusTabDTO[];
	/** Whether or not the focus has "Locked Focus" enabled. */
	tabsLocked: boolean;
	/** Name of the focus if it was saved to the focus library */
	name?: string;
	/** ID of the class it was applied to if it was applied */
	classId?: string;
	/** IDs of the students it was applied to if it was applied. Omitted in API response if focus is not active. */
	studentIds?: string[];
	/** Qoria User IDs of the students it was applied to if it was applied. */
	userIds?: string[];
	/** When the focus was last used */
	lastUsed?: number;
	/** When the focus was created */
	createdAt?: number;
	/** If the focus is active */
	active?: boolean;
}

export interface CreateOrUpdateFocusBadRequestResponseBody {
	errors: {
		/**
		 * Denotes if the name given to the Focus Library exceeds the charactor limit.
		 */
		characterLimitExceeded: boolean;
	};
}

export interface CreateOrUpdateFocusConflictResponseBody {
	errors: {
		/**
		 * Denotes if the name given to the Focus Library is already used.
		 */
		duplicatedName: boolean;
		/**
		 * Signature-based focus resource entries conflicting with School Manager locked rules.
		 */
		conflictingFocusTabSignatures: string[];
		/**
		 * URL-based focus resource entries conflicting with School Manager locked rules.
		 */
		conflictingFocusTabUrls: string[];
	};
}

/**
 * Used to populate the Focus Library Drawer
 */
export async function getFocusLibrary(
	teacherId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetFocusLibraryResponseBody> {
	const response = await fetch(`${host}/v1/teachers/${teacherId}/focus-library`, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	switch (response.status) {
		case 200:
			return (await response.json()) as GetFocusLibraryResponseBody;
		case 401:
		case 403:
			throw new Error("Unauthorized");
		default:
			throw new Error(`Failed to get focus library: ${response.statusText}`);
	}
}

export async function getFocusesAppliedToClass(
	classId: string,
	teacherId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<GetFocusesAppliedToClassResponseBody> {
	const response = await fetch(`${host}/v1/teachers/${teacherId}/classrooms/${classId}/applied-focuses`, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	switch (response.status) {
		case 200:
			return (await response.json()) as GetFocusesAppliedToClassResponseBody;
		case 401:
		case 403:
			throw new Error("Unauthorized");
		default:
			throw new Error(`Failed to get active focuses for class: ${response.statusText}`);
	}
}

export async function createOrUpdateFocus(
	resources: FocusTabDTO[],
	tabsLocked: boolean,
	applyImmediately: boolean,
	saveToLibrary: boolean,
	teacherId: string,
	/** Name of the focus. Only required if focus is being saved to the focus library. */
	name?: string,
	classId?: string,
	studentIds?: string[],
	/** ID of the focus to be updated. Undefined if creating a new focus. */
	focusId?: string,
	userIds?: string[],
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<CreateOrUpdateFocusOkResponseBody> {
	let apply = false;
	let temporary = false;

	let payload: CreateOrUpdateFocusRequestBody = {
		focusTabs: resources,
		tabsLocked: tabsLocked,
	};

	if (focusId) {
		payload = {
			...payload,
			id: focusId,
		};
	}

	if (applyImmediately) {
		apply = true;
		payload = {
			...payload,
			studentIds: studentIds,
			userIds: userIds,
		};
	}

	if (saveToLibrary) {
		payload = {
			...payload,
			name: name,
		};
	} else {
		temporary = true;
	}

	// Used when updating an existing focus
	if (classId) {
		payload = {
			...payload,
			classId: classId,
		};
	}

	const queryString = new URLSearchParams();
	queryString.append("apply", apply.toString());
	queryString.append("temporary", temporary.toString());

	const response = await fetch(`${host}/v1/teachers/${teacherId}/focus-library?${queryString.toString()}`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
		body: JSON.stringify(payload),
	});

	switch (response.status) {
		case 200:
			return (await response.json()) as CreateOrUpdateFocusOkResponseBody;
		case 400:
			throw new Error("Request validation failed");
		case 401:
		case 403:
			throw new Error("Unauthorized");
		case 409: {
			const body = (await response.json()) as CreateOrUpdateFocusConflictResponseBody;
			throw new FocusValidationConflictError(
				body.errors.duplicatedName,
				body.errors.conflictingFocusTabSignatures,
				body.errors.conflictingFocusTabUrls
			);
		}
		default:
			throw new Error(`Failed to create or update focus: ${response.statusText}`);
	}
}

export async function endFocus(
	focusId: string,
	classId: string,
	teacherId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(`${host}/v1/teachers/${teacherId}/focus-library/${focusId}/classes/${classId}/end`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	if (response.status !== 204) {
		throw new Error(`Failed to end focus: ${response.statusText}`);
	}
}

export async function deleteSavedFocusFromLibrary(
	focusId: string,
	teacherId: string,
	authHeaders = clsApi.authHeaders(),
	host = getClassroomApiUrl(window.location.hostname)
): Promise<void> {
	const response = await fetch(`${host}/v1/teachers/${teacherId}/focus-library/${focusId}`, {
		method: "DELETE",
		headers: {
			"Content-Type": "application/json",
			Authorization: authHeaders.Authorization,
			Device: authHeaders.Device,
		},
	});

	switch (response.status) {
		case 204:
			return;
		case 400:
			throw new Error("Focus is currently active in a class");
		case 401:
		case 403:
			throw new Error("Unauthorized");
		default:
			throw new Error(`Failed to delete focus from library: ${response.statusText}`);
	}
}
